/**
 * Scoring utilities for mini-games
 */

export interface StarRating {
  stars: number
  label: string
  color: number
}

/**
 * Calculate star rating based on score percentage
 */
export function calculateStars(score: number, maxScore: number): StarRating {
  if (maxScore <= 0) return { stars: 0, label: 'Try Again', color: 0x999999 }

  const percent = score / maxScore

  if (percent >= 0.9) {
    return { stars: 3, label: 'Perfect!', color: 0xffd700 }
  }
  if (percent >= 0.7) {
    return { stars: 2, label: 'Great!', color: 0xc0c0c0 }
  }
  if (percent >= 0.5) {
    return { stars: 1, label: 'Good', color: 0xcd7f32 }
  }
  return { stars: 0, label: 'Try Again', color: 0x999999 }
}

/**
 * Format score with commas
 */
export function formatScore(score: number): string {
  return score.toLocaleString()
}

/**
 * Calculate bonus points based on timing
 * @param elapsed Time elapsed in ms
 * @param target Target time in ms
 * @param maxBonus Maximum bonus points
 */
export function calculateTimingBonus(
  elapsed: number,
  target: number,
  maxBonus: number = 50
): number {
  const diff = Math.abs(elapsed - target)
  const tolerance = target * 0.2 // 20% tolerance

  if (diff <= tolerance * 0.5) return maxBonus // Perfect
  if (diff <= tolerance) return Math.floor(maxBonus * 0.5) // Good
  return 0
}

/**
 * Calculate accuracy bonus based on distance from target
 * @param distance Distance from target
 * @param maxDistance Maximum acceptable distance
 * @param maxBonus Maximum bonus points
 */
export function calculateAccuracyBonus(
  distance: number,
  maxDistance: number,
  maxBonus: number = 50
): number {
  if (distance >= maxDistance) return 0

  const accuracy = 1 - distance / maxDistance
  return Math.floor(maxBonus * accuracy)
}
